import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import { MetadataField, App } from './AppManager';
import { LOCATION_OPTIONS } from './config/locationConfig';

// Available app types
const AVAILABLE_TYPES = [
  "single-store",
  "single-store-multi-location", 
  "multi-store"
];

interface MetadataCollection {
  permissions: {
    read: string | string[];
    write: string | string[];
  };
  fields: MetadataField[];
}

interface AppListProps {
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onSelectApp: (appId: string) => void;
  canApproveDeployment: boolean;
  onViewAdminPanel?: () => void;
  selectedOrganizationId: string;
  onBackToOrganizations: () => void;
  onViewPermissions?: () => void;
}

const AppList: React.FC<AppListProps> = ({ 
  db, 
  functions, 
  onError, 
  onSuccess, 
  setLoading,
  onSelectApp,
  canApproveDeployment,
  onViewAdminPanel,
  selectedOrganizationId,
  onBackToOrganizations,
  onViewPermissions
}) => {
  const { currentUser, userPermissions } = useAuth();
  const [apps, setApps] = useState<MetadataField[]>([]);
  const [showLocationSelector, setShowLocationSelector] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  
  // Cloud function references - REMOVED createMinimalAppFunc
  // No cloud functions needed for creating the initial app record
  
  // Check if user can create apps for this organization
  const canCreateApps = (): boolean => {
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_write` ||
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Check if user has admin access to this organization
  const hasAdminAccess = (): boolean => {
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Get organization name (you might want to fetch this from Firestore)
  const getOrganizationName = (): string => {
    // For now, return the ID. You might want to fetch actual names from Firestore
    return selectedOrganizationId;
  };
  
  // Generate a temporary app ID
  const generateTempAppId = (): string => {
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    return `temp-${timestamp}`;
  };

  // Update region when location changes
  useEffect(() => {
    const location = LOCATION_OPTIONS.find(loc => loc.id === selectedLocation);
    if (location) {
      // Region is set automatically based on location
    }
  }, [selectedLocation]);
  
  // Load apps from metadata (filtered by organization)
  useEffect(() => {
    const loadApps = async () => {
      if (!currentUser || !selectedOrganizationId) return;
      
      setLoading(true);
      try {
        const metadataRef = doc(db, 'metadata', 'apps');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          const allApps = metadataData.fields || [];
          
          // Filter apps by organization
          const orgApps = allApps.filter(app => {
            return (app as any).organizationId === selectedOrganizationId;
          });
          
          setApps(orgApps);
        } else {
          setApps([]);
        }
        
      } catch (err) {
        console.error('Error loading apps:', err);
        onError('Failed to load apps. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadApps();
  }, [db, currentUser, selectedOrganizationId, setLoading, onError]);

  // Handle Add App button click - NO FIREBASE CALLS, just navigate to workflow
  const handleAddAppClick = () => {
    if (!canCreateApps()) {
      onError('You do not have permission to create apps for this organization.');
      return;
    }

    // Check if organization has locations configured
    const orgLocations = LOCATION_OPTIONS.filter(location => 
      location.organizationId === selectedOrganizationId
    );

    if (orgLocations.length === 0) {
      onError('No locations configured for this organization. Please contact an administrator.');
      return;
    }

    if (orgLocations.length === 1) {
      // Auto-select the only location and go to workflow immediately
      goToWorkflowWithNewApp(orgLocations[0].id);
    } else {
      // Show location selector
      setShowLocationSelector(true);
    }
  };

  // Go directly to workflow with new app - NO FIREBASE CALLS
  const goToWorkflowWithNewApp = (locationId: string) => {
    // Generate temporary app ID
    const tempAppId = generateTempAppId();
    
    // Simply navigate to workflow with the new app ID
    // The workflow will handle this as a "new app" case
    onSelectApp(tempAppId);
    
    // Store the location ID somewhere the workflow can access it
    // You might want to pass this through a prop or state
    sessionStorage.setItem('newAppLocationId', locationId);
    sessionStorage.setItem('newAppOrgId', selectedOrganizationId);
    
    // Reset location selector
    setShowLocationSelector(false);
    setSelectedLocation('');
  };

  // Handle location selection and go to workflow
  const handleLocationSelection = () => {
    if (!selectedLocation) {
      onError('Please select a location.');
      return;
    }
    goToWorkflowWithNewApp(selectedLocation);
  };

  return (
    <>
      <div className="applist-content-header">
        <h1 className="applist-page-title">App Management</h1>
        <div className="applist-header-actions">
          {canCreateApps() && (
            <button
              type="button"
              className="applist-add-app-btn"
              onClick={handleAddAppClick}
            >
              + Add App
            </button>
          )}
        </div>
      </div>

      <div className="applist-content-body">
        {/* Location Selector Modal */}
        {showLocationSelector && (
          <div className="applist-modal-overlay">
            <div className="applist-modal-container">
              <div className="applist-modal-content">
                <div className="applist-modal-header">
                  <h3>Select Location</h3>
                  <button 
                    type="button" 
                    className="applist-modal-close"
                    onClick={() => setShowLocationSelector(false)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="applist-modal-body">
                  <p>Choose the location for your new app:</p>
                  
                  <div className="applist-location-selector">
                    {LOCATION_OPTIONS
                      .filter(location => location.organizationId === selectedOrganizationId)
                      .map(location => (
                        <div 
                          key={location.id} 
                          className={`applist-location-option ${selectedLocation === location.id ? 'selected' : ''}`}
                          onClick={() => setSelectedLocation(location.id)}
                        >
                          <div className="applist-location-info">
                            <h4>{location.locationName}</h4>
                            <p>{location.country} • {location.currencyCode}</p>
                            <small>Region: {location.appRegion}</small>
                          </div>
                          <div className="applist-location-radio">
                            <input 
                              type="radio" 
                              checked={selectedLocation === location.id}
                              onChange={() => setSelectedLocation(location.id)}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                
                <div className="applist-modal-footer">
                  <button
                    type="button"
                    className="applist-modal-confirm-btn"
                    onClick={handleLocationSelection}
                    disabled={!selectedLocation}
                  >
                    Create App
                  </button>
                  <button
                    type="button"
                    className="applist-modal-cancel-btn"
                    onClick={() => setShowLocationSelector(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="applist-app-cards">
          {apps.length > 0 ? (
            apps.map(app => (
              <div
                key={app.id}
                className="applist-app-card"
              >
                <div className="applist-card-content">
                  <h3 className="applist-card-title">{app.name}</h3>
                  <div className="applist-card-status">
                    <span className={`applist-status-indicator ${app.enabled ? 'applist-status-enabled' : 'applist-status-disabled'}`}>
                      {app.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="applist-card-id">
                    ID: {app.id}
                  </div>
                  <div className="applist-card-footer">
                    <button 
                      type="button"
                      className="applist-view-workflow-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectApp(app.id);
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="applist-empty-state">
              <p>No apps available for this organization. {canCreateApps() ? 'Create your first app to get started.' : 'Contact an administrator to create apps.'}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AppList;